import { useRef } from 'react';
import { Button, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './copy.module.css';

/**
 * The manual copy (spec §7): the browser refused the clipboard, so the text
 * is shown selected for Ctrl+C / ⌘+C. Plain text only: formatting such as
 * tables may be lost this way, which the dialog says.
 */
const CopyFallbackDialog = ({
  text,
  onClose,
}: {
  /** Null: closed. */
  text: string | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const field = useRef<HTMLTextAreaElement>(null);

  // After the open animation: the text is selected, ready for Ctrl+C.
  const select = (open: boolean) => {
    if (!open) return;
    field.current?.focus();
    field.current?.select();
  };

  return (
    <Modal
      open={text !== null}
      title={t('v2.messages.copyTitle')}
      onCancel={onClose}
      footer={<Button onClick={onClose}>{t('v2.common.close')}</Button>}
      width="min(640px, calc(100vw - 32px))"
      afterOpenChange={select}
      destroyOnClose
    >
      <div data-testid="copy-fallback">
        <p className={styles.note}>{t('v2.messages.copyFailed')}</p>
        <p className={styles.note}>{t('v2.export.copyFormatNote')}</p>
        <textarea
          ref={field}
          className={styles.fallbackText}
          readOnly
          rows={12}
          value={text ?? ''}
          aria-label={t('v2.messages.copyTitle')}
          onFocus={event => event.target.select()}
        />
      </div>
    </Modal>
  );
};

export default CopyFallbackDialog;
